import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import EmployeeCard from '@/platform/common/components/EmployeeCard/EmployeeCard';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDate } from '@/platform/lib/utils';
import {
    Container,
    FilterBarDropdown,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { InformationCircleIcon } from '@heroicons/react/16/solid';
import { usePage } from '@inertiajs/react';
import Navigation from './components/Navigation';

const TYPES = [
    { id: '-1', name: 'Alle' },
    { id: 'completed', name: 'Voltooid' },
    { id: 'not-completed', name: 'Niet voltooid' },
];

const Hint = ({ children }) => (
    <Tooltip>
        <TooltipTrigger>
            <InformationCircleIcon className='ml-1 inline size-4 text-gray-400' />
        </TooltipTrigger>
        <TooltipContent>{children}</TooltipContent>
    </Tooltip>
);

const OverviewView = () => {
    const { auth, missing_data } = usePage().props;
    const { filters, updateFilters } = useFilters(['missing_data']);

    const showEntity = (filters?.entity_ids?.length || 0) > 1;
    const columns = showEntity ? 7 : 6;

    return (
        <Container fluid>
            <Navigation />

            <Segment>
                <SegmentHeader
                    header={`Ontbrekende gegevens (${missing_data?.meta?.total || 0})`}
                />
                <SegmentContent>
                    <SearchBarFilter only={['missing_data']}>
                        <FilterBarDropdown
                            multiple
                            selectAll
                            label={`Entiteiten (${filters?.entity_ids?.length || 0}/${auth?.customer?.entities?.length || 0})`}
                            options={auth.customer?.entities?.map(entity => ({
                                id: entity.id,
                                name: entity.name,
                            }))}
                            value={filters?.entity_ids || []}
                            onChange={value =>
                                updateFilters('entity_ids', value)
                            }
                        />
                        <FilterBarDropdown
                            label={`Type (${TYPES.find(type => type.id == (filters?.type || ''))?.name || 'Alle'})`}
                            options={TYPES}
                            value={filters?.type || '-1'}
                            onChange={value =>
                                updateFilters(
                                    'type',
                                    value === '-1' ? null : value
                                )
                            }
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                {showEntity && (
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                )}
                                <TableHeaderCell>
                                    Ontbrekende velden
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Ingevulde velden
                                    <Hint>
                                        Een lijst met velden die handmatig zijn
                                        ingevuld door de klant.
                                    </Hint>
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Ingevuld door
                                    <Hint>
                                        Dit is alleen aanwezig als ontbrekende
                                        gegevens handmatig zijn ingevuld.
                                    </Hint>
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Ingevuld op
                                    <Hint>
                                        De datum waarop alle ontbrekende
                                        informatie is ingevuld.
                                    </Hint>
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Is uitgesloten
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {missing_data?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={columns} />
                            )}

                            {missing_data?.data?.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        <EmployeeCard
                                            employee={row?.employee || ''}
                                            link
                                        />
                                    </TableCell>
                                    {showEntity && (
                                        <TableCell>
                                            {row.entity?.name}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <ul className='list-inside list-disc'>
                                            {row.requested_fields_labels?.map(
                                                field => (
                                                    <li key={field}>{field}</li>
                                                )
                                            )}
                                        </ul>
                                    </TableCell>
                                    <TableCell>
                                        <ul className='list-inside list-disc'>
                                            {row.filled_fields?.map(field => (
                                                <li key={field}>{field}</li>
                                            ))}
                                        </ul>
                                    </TableCell>
                                    <TableCell>{row.filled_by?.name}</TableCell>
                                    <TableCell>
                                        {localDate(row.filled_at)}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {row.is_excluded ? 'Ja' : 'Nee'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination
                        meta={missing_data?.meta}
                        only={['missing_data']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default OverviewView;
